import { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import useDatasets from "datasets/hooks/useDatasets";
import TextOverflow from "~/components/TextOverflow";
import rumClient, { trackEvent } from "~/utils/monitoring";

const { VITE_CATALOG_API } = import.meta.env;

const DeleteDatasetModal = ({
  id,
  name,
  onClose,
  open,
}: {
  id: string;
  name: string;
  onClose: () => void;
  open: boolean;
}) => {
  const navigate = useNavigate();
  const { mutate } = useDatasets();
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    try {
      const resp = await fetch(`${VITE_CATALOG_API}/datasets/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ is_deleted: true }),
        headers: { "Content-Type": "application/json" },
      });

      if (!resp.ok) {
        throw Error("There was a problem trying to archive dataset");
      }

      trackEvent("dataset_deleted", { dataset_id: id });
      mutate((currentData) => currentData?.filter((dataset) => dataset.id !== id));
      navigate("/data-browser/datasets");
    } catch (e) {
      console.error(e);
      rumClient?.recordError(e);
      setError("Unable to archive dataset");
    }

    setSubmitting(false);
  };

  return (
    <Dialog fullWidth open={open} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          <TextOverflow>{`Archive "${name}"`}</TextOverflow>
        </DialogTitle>
        <DialogContent sx={{ gap: 2, fontSize: 14 }}>
          {error && <Alert severity="error">{error}</Alert>}
          Archive and hide this dataset. An admin can restore if necessary.
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={submitting}
            sx={{
              backgroundColor: "attention.main",
              "&:hover": { backgroundColor: "attention.dark" },
            }}
          >
            Archive
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default DeleteDatasetModal;
